import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const TELEGRAM_URL = 'https://t.me/rocketsaas_bot';

const FEATURE_KEYS = [
  'bot',
  'dashboard',
  'pricing',
  'discounts',
  'branding',
  'app',
] as const;

const STEP_KEYS = ['1', '2', '3', '4'] as const;
const FAQ_KEYS = ['botSite', 'reseller', 'ownPrices', 'app'] as const;

type Props = {
  variant?: 'landing' | 'app';
};

export function HomeRocketSaas({ variant = 'landing' }: Props) {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<string | null>('botSite');
  const isApp = variant === 'app';

  return (
    <section
      className={`dx-saas${isApp ? ' dx-saas--app' : ''}`}
      id="rocketsaas"
      aria-labelledby="saas-heading"
    >
      <div className="dx-saas__hero">
        <span className="dx-saas__brand">RocketSaaS</span>
        <h2 id="saas-heading">{t('home.rocketSaas.hero.title')}</h2>
        <p className="dx-saas__lead">{t('home.rocketSaas.hero.lead')}</p>
        <p className="dx-saas__sub">{t('home.rocketSaas.hero.sub')}</p>
        <div className="dx-saas__actions">
          <a
            href={TELEGRAM_URL}
            className="dx-btn dx-btn--primary"
            target="_blank"
            rel="noreferrer"
          >
            {t('home.rocketSaas.hero.ctaRequest')}
          </a>
          <a href="#saas-features" className="dx-btn dx-btn--ghost">
            {t('home.rocketSaas.hero.ctaFeatures')}
          </a>
        </div>
        <div className="dx-saas__pills" aria-hidden="true">
          <span>{t('home.rocketSaas.pills.bot')}</span>
          <span>{t('home.rocketSaas.pills.website')}</span>
          <span>{t('home.rocketSaas.pills.discounts')}</span>
        </div>
      </div>

      <div className="dx-saas__block" id="saas-features">
        <h3>{t('home.rocketSaas.features.title')}</h3>
        <ul className="dx-saas__features">
          {FEATURE_KEYS.map((key) => {
            const soon = key === 'app';
            return (
              <li key={key} className={soon ? 'is-soon' : undefined}>
                <span className="dx-saas__check" aria-hidden="true" />
                <div>
                  <strong>
                    {t(`home.rocketSaas.features.${key}.title`)}
                    {soon ? (
                      <em className="dx-saas__soon">{t('home.rocketSaas.features.soon')}</em>
                    ) : null}
                  </strong>
                  <p>{t(`home.rocketSaas.features.${key}.desc`)}</p>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="dx-saas__tiers-slot" data-ready-for="discount-tiers" hidden />
      </div>

      <div className="dx-saas__block dx-saas__why">
        <h3>{t('home.rocketSaas.why.title')}</h3>
        <p>{t('home.rocketSaas.why.p1')}</p>
        <p>{t('home.rocketSaas.why.p2')}</p>
      </div>

      <div className="dx-saas__block">
        <h3>{t('home.rocketSaas.steps.title')}</h3>
        <ol className="dx-saas__steps">
          {STEP_KEYS.map((key) => (
            <li key={key}>
              <span className="dx-saas__step-num">{key}</span>
              <span>{t(`home.rocketSaas.steps.${key}`)}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="dx-saas__offer">
        <h3>{t('home.rocketSaas.offer.title')}</h3>
        <p className="dx-saas__offer-main">{t('home.rocketSaas.offer.main')}</p>
        <p>{t('home.rocketSaas.offer.note')}</p>
        <a
          href={TELEGRAM_URL}
          className="dx-btn dx-btn--primary"
          target="_blank"
          rel="noreferrer"
        >
          {t('home.rocketSaas.offer.cta')}
        </a>
      </div>

      <div className="dx-saas__block">
        <h3>{t('home.rocketSaas.faq.title')}</h3>
        <div className="dx-faq__list">
          {FAQ_KEYS.map((key) => {
            const isOpen = openFaq === key;
            return (
              <div key={key} className={`dx-faq__item ${isOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="dx-faq__q"
                  aria-expanded={isOpen}
                  onClick={() => setOpenFaq(isOpen ? null : key)}
                >
                  <span>{t(`home.rocketSaas.faq.${key}.q`)}</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {isOpen ? (
                  <div className="dx-faq__a">
                    <p>{t(`home.rocketSaas.faq.${key}.a`)}</p>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="dx-saas__finale">
        <h3>{t('home.rocketSaas.finale.title')}</h3>
        <p className="dx-saas__finale-tag">{t('home.rocketSaas.finale.tag')}</p>
        <p>{t('home.rocketSaas.finale.body')}</p>
        <a
          href={TELEGRAM_URL}
          className="dx-btn dx-btn--primary"
          target="_blank"
          rel="noreferrer"
        >
          {t('home.rocketSaas.finale.cta')}
        </a>
      </div>
    </section>
  );
}
